import React from 'react';
import { Link } from 'react-router-dom';
import { Match, MatchPair } from '../api/matches';

interface Props {
  matches: Match[];
}

const PHASE_ORDER = ['round_of_16', 'quarterfinal', 'semifinal', 'final', 'third_place'];
const PHASE_LABEL: Record<string, string> = {
  round_of_16: 'Achtelfinale',
  quarterfinal: 'Viertelfinale',
  semifinal: 'Halbfinale',
  final: 'Finale',
  third_place: 'Platz 3',
};

function pairLabel(pair?: MatchPair | null): string {
  if (!pair) return 'TBD';
  const p1 = pair.player1;
  const p2 = pair.player2;
  return `${p1 ? p1.first_name : '?'} / ${p2 ? p2.first_name : '?'}`;
}

function MatchBox({ match }: { match: Match }) {
  const isConfirmed = match.status === 'confirmed';
  const isPairMatch = !!(match.home_pair_id || match.away_pair_id);

  function UnitRow({ label, isWinner }: { label: string; isWinner: boolean }) {
    return (
      <div className={`flex items-center justify-between px-3 py-1.5 border-b border-gray-100 last:border-0 ${isWinner ? 'bg-primary-50' : ''}`}>
        <span className={`text-sm truncate max-w-[140px] ${isWinner ? 'font-semibold text-primary-700' : label === 'TBD' ? 'text-gray-300' : 'text-gray-700'}`}>
          {label}
        </span>
        {isWinner && (
          <svg className="w-4 h-4 text-primary-500 flex-shrink-0 ml-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
    );
  }

  const homeLabel = isPairMatch
    ? pairLabel(match.home_pair)
    : match.home_player ? `${match.home_player.first_name} ${match.home_player.last_name}` : 'TBD';
  const awayLabel = isPairMatch
    ? pairLabel(match.away_pair)
    : match.away_player ? `${match.away_player.first_name} ${match.away_player.last_name}` : 'TBD';

  const homeIsWinner = isConfirmed && (
    isPairMatch ? match.winner_pair_id === match.home_pair_id && !!match.home_pair_id
                : match.winner_id === match.home_player_id && !!match.home_player_id
  );
  const awayIsWinner = isConfirmed && (
    isPairMatch ? match.winner_pair_id === match.away_pair_id && !!match.away_pair_id
                : match.winner_id === match.away_player_id && !!match.away_player_id
  );

  return (
    <Link to={`/matches/${match.id}`} className="block hover:shadow-md transition-shadow">
      <div className="border border-gray-200 rounded-md bg-white overflow-hidden w-52">
        <UnitRow label={homeLabel} isWinner={homeIsWinner} />
        <UnitRow label={awayLabel} isWinner={awayIsWinner} />
        {match.result && isConfirmed && (
          <div className="px-3 py-1 bg-gray-50 text-xs text-center text-gray-400">
            {match.result.home_sets_won} : {match.result.away_sets_won}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function KoBracket({ matches }: Props) {
  // Group by phase
  const byPhase: Record<string, Match[]> = {};
  for (const m of matches) {
    if (m.phase === 'group') continue;
    if (!byPhase[m.phase]) byPhase[m.phase] = [];
    byPhase[m.phase].push(m);
  }

  // Sort each phase by match_number
  for (const phase of Object.keys(byPhase)) {
    byPhase[phase].sort((a, b) => (a.match_number ?? 0) - (b.match_number ?? 0));
  }

  const mainPhases = PHASE_ORDER.filter(
    (p) => p !== 'third_place' && byPhase[p]?.length > 0
  );
  const hasThirdPlace = byPhase['third_place']?.length > 0;

  if (mainPhases.length === 0) {
    return (
      <div className="text-center text-gray-400 py-12">
        Keine K.O.-Spiele vorhanden
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-8 p-4 min-w-max">
        {mainPhases.map((phase) => (
          <div key={phase} className="flex flex-col">
            <h4 className="text-sm font-semibold text-gray-600 mb-4 text-center">
              {PHASE_LABEL[phase] ?? phase}
            </h4>
            <div className="flex flex-col gap-4 justify-around flex-1">
              {byPhase[phase].map((m) => (
                <MatchBox key={m.id} match={m} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {hasThirdPlace && (
        <div className="mt-4 p-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-600 mb-3">Spiel um Platz 3</h4>
          <div className="flex gap-4">
            {byPhase['third_place'].map((m) => (
              <MatchBox key={m.id} match={m} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
